import { createAuthenticatedGraphQLClient } from '../../auth/createAuthenticatedGraphQLClient';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import type {
  CanvassingSessionTranscriptionQuery,
  CanvassingSessionTranscriptionQueryVariables,
} from '../../graphql/queries/canvassingSessionTranscription.generated';
import type {
  TranscribeCanvassingSessionMutation,
  TranscribeCanvassingSessionMutationVariables,
} from '../../graphql/queries/transcribeCanvassingSession.generated';
import { GraphQLRequestError } from '../../graphql/routing/apiRouting';
import {
  CANVASSING_SESSION_TRANSCRIPTION_DOCUMENT,
  TRANSCRIBE_CANVASSING_SESSION_DOCUMENT,
} from '../canvassingSessionDocuments';
import type {
  CanvassingSessionTranscriptionPublication,
  TranscribeCanvassingSessionResult,
} from './sessionTranscriptionTypes';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';

export type CanvassingSessionTranscriptionApi = {
  fetchTranscription: (
    input: CanvassingSessionTranscriptionQueryVariables,
    options: { signal: AbortSignal },
  ) => Promise<CanvassingSessionTranscriptionPublication | null>;
  transcribe: (
    input: TranscribeCanvassingSessionMutationVariables,
    options: { signal: AbortSignal },
  ) => Promise<TranscribeCanvassingSessionResult | null>;
};

export type CanvassingSessionTranscriptionError = {
  kind: 'forbidden' | 'request';
  failureCode: string | null;
};

const readExtensionCode = (value: unknown): string | null => {
  if (typeof value !== 'object' || value === null) return null;
  const extensions = Reflect.get(value, 'extensions');
  if (typeof extensions !== 'object' || extensions === null) return null;
  const code = Reflect.get(extensions, 'code');
  return typeof code === 'string' ? code : null;
};

export const getCanvassingSessionTranscriptionError = (error: unknown): CanvassingSessionTranscriptionError => {
  if (!(error instanceof GraphQLRequestError)) return { kind: 'request', failureCode: null };
  const codes = (error.graphQLErrors ?? []).map(readExtensionCode);
  if (error.status === 403 || codes.includes('FORBIDDEN')) return { kind: 'forbidden', failureCode: 'FORBIDDEN' };
  return { kind: 'request', failureCode: codes.find(code => code !== null) ?? null };
};

export const createCanvassingSessionTranscriptionApi = (
  fetchImpl?: CanvassingSessionFetchImpl,
): CanvassingSessionTranscriptionApi => ({
  fetchTranscription: async ({ sessionId, includeResult }, { signal }) => {
    const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
    const variables: CanvassingSessionTranscriptionQueryVariables = { sessionId, includeResult };
    const response = await client.request<CanvassingSessionTranscriptionQuery>({
      document: CANVASSING_SESSION_TRANSCRIPTION_DOCUMENT,
      variables,
      signal,
    });
    const publication = response.canvassingSessionTranscription;
    return publication === null ? null : { ...publication, result: publication.result ?? null };
  },
  transcribe: async ({ sessionId, requestId }, { signal }) => {
    const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
    const variables: TranscribeCanvassingSessionMutationVariables = { sessionId, requestId };
    const response = await client.request<TranscribeCanvassingSessionMutation>({
      document: TRANSCRIBE_CANVASSING_SESSION_DOCUMENT,
      variables,
      signal,
    });
    return response.transcribeCanvassingSession;
  },
});
